import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import { ArrowLeft, BarChart3, ArrowUp, ArrowDown, Minus, FileText, Trophy, ClipboardCheck, PiggyBank, Camera } from 'lucide-react';

// =====================================================================
// INDICADORES (por loja) — multi-área em ABAS
// =====================================================================
// Aberta pelo atalho "Indicadores" do Acesso Rápido. Cada aba carrega seus
// dados sob demanda (lazy), de um endpoint próprio:
//   - Auditoria & Treinamento → /api/home/indicadores-formularios
//   - Financeiro               → /api/home/indicadores-financeiro
// Recorte por loja vem do backend (Gerente só a sua); `escritorio` libera o
// ranking (na aba de formulários) só p/ quem vê a rede.
// VISÃO DA LOJA: na aba Auditoria & Treinamento, o gerente (não-escritório) recebe
// um painel aprofundado (pontuação por área da última auditoria + itens "não
// atende" + recomendações/PDF + histórico/evolução) — /api/home/indicadores-loja-detalhe.

const COR = {
    paginaFundo: '#f2f2f2', branco: '#fff', vermelhoEscuro: '#990605', amarelo: '#FDBB20',
    texto: '#1a1a1a', bordaSuave: 'rgba(199,194,194,0.45)',
    verde: '#16a34a', verdeFundo: '#dcfce7', amareloFundo: '#fef9c3', vermelho: '#dc2626', vermelhoFundo: '#fee2e2',
    cinza: '#64748b', cinzaFundo: '#f1f5f9',
};
const FONTE = "'Raleway', sans-serif";
const SOMBRA = '0 4px 8px rgba(0,0,0,0.12)';
const VERDE = { fg: COR.verde, bg: COR.verdeFundo };
const AMARELO = { fg: '#a16207', bg: COR.amareloFundo };
const VERMELHO = { fg: COR.vermelho, bg: COR.vermelhoFundo };
const CINZA = { fg: COR.cinza, bg: COR.cinzaFundo };

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const ddmm = (s) => (s ? s.slice(8, 10) + '/' + s.slice(5, 7) : '');
const fmt = (n) => (typeof n === 'number' ? parseFloat(n.toFixed(2)) : n);
const corPct = (pct) => (pct >= 90 ? VERDE : pct >= 70 ? AMARELO : VERMELHO);
// Cor da classificação de CONFORMIDADE do checklist (as faixas da Avaliação BSV).
// ⚠️ Era risco (Baixo/Medio/Critico/AltoRisco) até 2026-09-04; o checklist virou
// conformidade — maior é melhor — e as auditorias antigas foram convertidas no banco.
const corClassificacao = (classe) => {
    const c = (classe || '').toLowerCase();
    if (c.startsWith('excelente')) return VERDE;
    if (c.startsWith('satisfat')) return VERDE;
    if (c.startsWith('atencao') || c.startsWith('atenç')) return AMARELO;
    if (c.startsWith('critico') || c.startsWith('crít')) return VERMELHO;
    return CINZA;   // NA / desconhecido
};

const ABAS = [
    { id: 'formularios', label: 'Auditoria & Treinamento', icon: ClipboardCheck },
    { id: 'financeiro', label: 'Financeiro', icon: PiggyBank },
];

function IndicadoresFormularios() {
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [aba, setAba] = useState('formularios');

    // Dados por aba (carga lazy) + flags de carregamento.
    const [form, setForm] = useState(null);
    const [detalhe, setDetalhe] = useState(null);   // visão aprofundada da loja (não-escritório)
    const [fin, setFin] = useState(null);
    const [carregando, setCarregando] = useState(false);

    useEffect(() => {
        const onResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', onResize); onResize();
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const buscar = useCallback(async (url, setter) => {
        setCarregando(true);
        try {
            const r = await fetch(url, { headers: authHeaders() });
            if (r.ok) setter(await r.json());
        } catch (e) { console.error('[Indicadores]', url, e); }
        finally { setCarregando(false); }
    }, []);

    // Carrega os dados da aba ativa só na 1ª vez que ela é aberta.
    useEffect(() => {
        if (aba === 'formularios' && !form) buscar('/api/home/indicadores-formularios', setForm);
        if (aba === 'financeiro' && !fin) buscar('/api/home/indicadores-financeiro', setFin);
    }, [aba, form, fin, buscar]);

    // Para o gerente (não-escritório), busca a visão aprofundada da loja.
    useEffect(() => {
        if (form && form.escritorio === false && !detalhe) buscar('/api/home/indicadores-loja-detalhe', setDetalhe);
    }, [form, detalhe, buscar]);

    const getMarginLeft = () => (window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'));

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: COR.paginaFundo, fontFamily: FONTE }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />

            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease', padding: '28px 32px', minWidth: 0 }}>
                <button onClick={() => navigate('/home')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: COR.vermelhoEscuro, fontFamily: FONTE, fontWeight: 600, fontSize: '14px', padding: 0, marginBottom: '14px' }}>
                    <ArrowLeft size={18} /> Voltar para a Home
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <BarChart3 size={28} color={COR.vermelhoEscuro} />
                    <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: COR.texto }}>Indicadores</h1>
                </div>

                {/* Abas */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                    {ABAS.map((a) => {
                        const ativa = aba === a.id;
                        return (
                            <button key={a.id} onClick={() => setAba(a.id)} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '9px 16px', borderRadius: '10px',
                                border: `1px solid ${ativa ? COR.vermelhoEscuro : COR.bordaSuave}`, cursor: 'pointer', fontFamily: FONTE,
                                fontWeight: 700, fontSize: '13px', backgroundColor: ativa ? COR.vermelhoEscuro : COR.branco, color: ativa ? '#fff' : COR.cinza,
                            }}>
                                <a.icon size={16} /> {a.label}
                            </button>
                        );
                    })}
                </div>

                {aba === 'formularios' && <AbaFormularios dados={form} detalhe={detalhe} carregando={carregando && !form} />}
                {aba === 'financeiro' && <AbaFinanceiro dados={fin} carregando={carregando && !fin} />}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// ABA — Auditoria & Treinamento
// ---------------------------------------------------------------------
function AbaFormularios({ dados, detalhe, carregando }) {
    if (carregando) return <Carregando />;
    if (!dados) return null;
    const { lojas, resumo, vencidaDias, escritorio } = dados;
    // Gerente (não-escritório): visão aprofundada da própria loja.
    if (!escritorio) return <VisaoLoja detalhe={detalhe} />;
    return (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '22px' }}>
                <CardResumo titulo="Auditoria (conformidade média)" valor={resumo.checklist.mediaConformidade != null ? `${fmt(resumo.checklist.mediaConformidade)}%` : '—'} rodape={`${resumo.checklist.criticas} em nível crítico`} />
                <CardResumo titulo="Auditoria SA (média)" valor={resumo.auditoriaSA.media != null ? `${fmt(resumo.auditoriaSA.media)}%` : '—'} rodape={`${resumo.auditoriaSA.vencidas} vencida(s)`} />
                <CardResumo titulo="Avaliação TSV (média)" valor={resumo.tsv.media != null ? `${fmt(resumo.tsv.media)}%` : '—'} rodape={`${resumo.tsv.vencidas} vencida(s)`} />
                <CardResumo titulo="Aplicações vencidas" valor={resumo.auditoriaSA.vencidas + resumo.tsv.vencidas + resumo.checklist.vencidas} rodape={`sem aplicar há +${vencidaDias} dias`} alerta />
            </div>

            <div style={{ backgroundColor: COR.branco, borderRadius: '10px', boxShadow: SOMBRA, overflow: 'hidden', marginBottom: '24px' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: '640px' }}>
                        <thead><tr style={{ backgroundColor: '#faf7f7' }}><Th>Loja</Th><Th>Auditoria (checklist)</Th><Th>Auditoria SA</Th><Th>Avaliação TSV</Th></tr></thead>
                        <tbody>
                            {lojas.length === 0 && <tr><td colSpan={4} style={{ padding: '28px', textAlign: 'center', color: '#999' }}>Sem dados no seu escopo.</td></tr>}
                            {lojas.map((l) => (
                                <tr key={l.loja} style={{ borderTop: `1px solid ${COR.bordaSuave}` }}>
                                    <Td><span style={{ fontWeight: 700, color: COR.texto }}>{l.loja}</span></Td>
                                    <Td><CelulaChecklist c={l.checklist} vencidaDias={vencidaDias} /></Td>
                                    <Td><CelulaPct c={l.auditoriaSA} vencidaDias={vencidaDias} /></Td>
                                    <Td><CelulaPct c={l.tsv} vencidaDias={vencidaDias} /></Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {escritorio && lojas.length > 1 && (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <Trophy size={20} color={COR.amarelo} />
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COR.texto }}>Ranking de lojas</h2>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                        <Ranking titulo="Auditoria (maior %)" lojas={lojas} get={(l) => l.checklist && l.checklist.conformidade} unidade="%" />
                        <Ranking titulo="Auditoria SA (maior %)" lojas={lojas} get={(l) => l.auditoriaSA && l.auditoriaSA.nota} unidade="%" />
                        <Ranking titulo="Avaliação TSV (maior %)" lojas={lojas} get={(l) => l.tsv && l.tsv.nota} unidade="%" />
                    </div>
                </div>
            )}
        </>
    );
}

// ---------------------------------------------------------------------
// ABA — Financeiro (petty cash / estornos)
// ---------------------------------------------------------------------
function AbaFinanceiro({ dados, carregando }) {
    if (carregando) return <Carregando />;
    if (!dados) return null;
    const lojas = dados.lojas || [];
    const limite = dados.limite || 600;
    const totAguardLoja = lojas.reduce((s, l) => s + (l.estornos ? l.estornos.aguardandoLoja : 0), 0);
    const perto = lojas.filter((l) => l.pettyCash && l.pettyCash.gasto >= limite * 0.9).length;
    const gastoMes = lojas.reduce((s, l) => s + (l.pettyCash ? l.pettyCash.gasto : 0), 0);
    const totEstornado = (dados.estornosStatus || []).reduce((s, e) => s + e.valor, 0);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '16px' }}>
                <CardResumo titulo="Gasto petty cash (mês)" valor={`R$ ${fmt(gastoMes)}`} rodape={`${lojas.length} loja(s)`} />
                <CardResumo titulo="Petty cash perto do limite" valor={perto} rodape={`≥ 90% de R$${limite}`} alerta={perto > 0} />
                <CardResumo titulo="Estornos aguardando loja" valor={totAguardLoja} rodape="retorno pendente" alerta={totAguardLoja > 0} />
                <CardResumo titulo="Total estornado" valor={`R$ ${fmt(totEstornado)}`} rodape="acumulado" />
            </div>

            <Secao titulo="Petty cash — evolução do gasto (12 meses)">
                <MiniBarras dados={dados.pettyCashEvolucao} valorDe={(d) => d.gasto} rotuloValor={(v) => fmt(v)} cor={COR.vermelhoEscuro} />
            </Secao>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                <Secao titulo="Estornos por status">
                    <EstornoStatus itens={dados.estornosStatus} />
                </Secao>
                <Secao titulo="Estornos — solicitações por mês (12 meses)">
                    <MiniBarras dados={dados.estornosEvolucao} valorDe={(d) => d.qtd} rotuloValor={(v) => v} cor={COR.amarelo} />
                </Secao>
            </div>

            <Secao titulo="Por loja">
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: '560px' }}>
                        <thead><tr style={{ backgroundColor: '#faf7f7' }}><Th>Loja</Th><Th>Petty cash (mês)</Th><Th>Estornos pendentes</Th></tr></thead>
                        <tbody>
                            {lojas.length === 0 && <tr><td colSpan={3} style={{ padding: '28px', textAlign: 'center', color: '#999' }}>Sem dados no seu escopo.</td></tr>}
                            {lojas.map((l) => (
                                <tr key={l.loja} style={{ borderTop: `1px solid ${COR.bordaSuave}` }}>
                                    <Td><span style={{ fontWeight: 700, color: COR.texto }}>{l.loja}</span></Td>
                                    <Td><PettyCash pc={l.pettyCash} limite={limite} /></Td>
                                    <Td><Estornos e={l.estornos} /></Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Secao>
        </div>
    );
}

// Mini-gráfico de barras mensais (12) com valor no topo e mês embaixo.
function MiniBarras({ dados, valorDe, rotuloValor, cor }) {
    if (!dados || !dados.length) return <span style={{ color: '#999' }}>—</span>;
    const vals = dados.map(valorDe);
    const max = Math.max(1, ...vals);
    const W = 600; const H = 120; const padX = 12; const padTop = 18; const padBot = 22;
    const n = dados.length;
    const passo = (W - 2 * padX) / n;
    const bw = passo * 0.6;
    const mesLabel = (m) => `${m.slice(5)}/${m.slice(2, 4)}`;
    return (
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', maxHeight: '170px' }}>
            {dados.map((d, i) => {
                const v = valorDe(d);
                const cx = padX + (i + 0.5) * passo;
                const h = (v / max) * (H - padTop - padBot);
                const y = (H - padBot) - h;
                return (
                    <g key={i}>
                        <rect x={cx - bw / 2} y={y} width={bw} height={Math.max(0, h)} fill={cor} rx="2" />
                        {v > 0 && <text x={cx} y={y - 4} textAnchor="middle" fontSize="9" fontWeight="700" fill={COR.texto}>{rotuloValor(v)}</text>}
                        <text x={cx} y={H - 5} textAnchor="middle" fontSize="8" fill="#aaa">{mesLabel(d.mes)}</text>
                    </g>
                );
            })}
        </svg>
    );
}

// Lista de estornos por status (contagem + valor), cor pelo tipo de status.
function EstornoStatus({ itens }) {
    if (!itens || !itens.length) return <span style={{ color: '#999' }}>Sem estornos no escopo.</span>;
    const corStatus = (s) => {
        const t = (s || '').toLowerCase();
        if (t.includes('efetuado')) return VERDE;
        if (t.includes('loja')) return VERMELHO;
        if (t.includes('financeiro')) return AMARELO;
        return CINZA;
    };
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {itens.map((it, i) => {
                const cor = corStatus(it.status);
                return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: cor.fg, flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: '13px', color: COR.texto, fontWeight: 600 }}>{it.status}</span>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: COR.texto }}>{it.qtd}</span>
                        <span style={{ width: '96px', textAlign: 'right', fontSize: '12px', color: '#999' }}>R$ {fmt(it.valor)}</span>
                    </div>
                );
            })}
        </div>
    );
}

// Barra de uso do petty cash (gasto vs limite), colorida por faixa.
function PettyCash({ pc, limite }) {
    if (!pc) return <span style={{ color: '#bbb' }}>—</span>;
    const pctUso = Math.min(100, Math.round((pc.gasto / limite) * 100));
    const cor = pctUso >= 90 ? VERMELHO : pctUso >= 70 ? AMARELO : VERDE;
    return (
        <div style={{ minWidth: '160px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '3px' }}>
                <span style={{ fontWeight: 700, color: cor.fg }}>R$ {fmt(pc.gasto)}</span>
                <span style={{ color: '#999' }}>de R$ {limite}</span>
            </div>
            <div style={{ height: '6px', borderRadius: '4px', backgroundColor: '#eee', overflow: 'hidden' }}>
                <div style={{ width: `${pctUso}%`, height: '100%', backgroundColor: cor.fg }} />
            </div>
        </div>
    );
}

// Badges de estornos pendentes (aguardando loja / financeiro)
function Estornos({ e }) {
    if (!e || (!e.aguardandoLoja && !e.aguardandoFin)) return <span style={{ color: COR.verde, fontSize: '13px' }}>Em dia</span>;
    return (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {e.aguardandoLoja > 0 && <span style={{ backgroundColor: COR.vermelhoFundo, color: COR.vermelho, fontWeight: 700, fontSize: '12px', borderRadius: '6px', padding: '2px 8px' }}>{e.aguardandoLoja} aguardando loja</span>}
            {e.aguardandoFin > 0 && <span style={{ backgroundColor: COR.amareloFundo, color: '#a16207', fontWeight: 700, fontSize: '12px', borderRadius: '6px', padding: '2px 8px' }}>{e.aguardandoFin} aguardando financeiro</span>}
        </div>
    );
}

// ---------------------------------------------------------------------
// VISÃO DA LOJA (gerente / não-escritório) — painel aprofundado
// ---------------------------------------------------------------------
function VisaoLoja({ detalhe }) {
    const [chip, setChip] = useState('checklist');   // hook antes de qualquer return
    if (!detalhe) return <Carregando />;
    const { checklist, auditoriaSA, tsv, historico } = detalhe;
    const CHIPS = [
        { id: 'checklist', label: 'Auditoria checklist' },
        { id: 'sa', label: 'Auditoria SA' },
        { id: 'tsv', label: 'Avaliação TSV' },
    ];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Chips: escolhe qual formulário detalhar (evita poluir tudo junto) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {CHIPS.map((c) => {
                    const ativo = chip === c.id;
                    return (
                        <button key={c.id} onClick={() => setChip(c.id)} style={{
                            padding: '8px 16px', borderRadius: '999px', cursor: 'pointer', fontFamily: FONTE, fontWeight: 700, fontSize: '13px',
                            border: `1px solid ${ativo ? COR.amarelo : COR.bordaSuave}`, backgroundColor: ativo ? COR.amarelo : COR.branco, color: ativo ? '#fff' : COR.cinza,
                        }}>{c.label}</button>
                    );
                })}
            </div>

            {chip === 'checklist' && <PainelChecklist checklist={checklist} historico={historico.checklist} />}
            {chip === 'sa' && <PainelSecoes dados={auditoriaSA} historico={historico.auditoriaSA} titulo="Auditoria SA" />}
            {chip === 'tsv' && <PainelSecoes dados={tsv} historico={historico.tsv} titulo="Avaliação TSV" />}
        </div>
    );
}

// Painel do checklist: última auditoria (áreas + itens a corrigir + PDF) + histórico.
function PainelChecklist({ checklist, historico }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <Secao titulo="Última auditoria (checklist)">
                {!checklist ? (
                    <div style={{ color: '#999', fontSize: '14px' }}>Nenhuma auditoria registrada para a sua loja.</div>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                            {(() => { const cor = corClassificacao(checklist.classificacao); return (
                                <span style={{ backgroundColor: cor.bg, color: cor.fg, fontWeight: 800, fontSize: '13px', borderRadius: '6px', padding: '3px 10px' }}>{checklist.classificacao} · {fmt(checklist.conformidade)}% de conformidade</span>
                            ); })()}
                            <span style={{ color: '#999', fontSize: '13px' }}>aplicada em {ddmm(checklist.data)}</span>
                            {checklist.pdf && <a href={checklist.pdf} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: COR.vermelhoEscuro, fontSize: '13px', fontWeight: 600 }}><FileText size={15} /> Relatório (PDF)</a>}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#777', marginBottom: '10px' }}>Conformidade por área (pior no topo)</div>
                        <AreaBars areas={checklist.areas} />
                        {checklist.recomendacoes && (
                            <div style={{ marginTop: '16px', backgroundColor: '#fffbeb', border: `1px solid ${COR.amareloFundo}`, borderRadius: '8px', padding: '10px 12px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#a16207', marginBottom: '4px' }}>Recomendações</div>
                                <div style={{ fontSize: '13px', color: '#555', whiteSpace: 'pre-wrap' }}>{checklist.recomendacoes}</div>
                            </div>
                        )}
                    </>
                )}
            </Secao>

            {checklist && checklist.naoAtende.length > 0 && (
                <Secao titulo={`Itens a corrigir (${checklist.naoAtende.length})`}>
                    {checklist.pdf && (
                        <a href={checklist.pdf} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '12px', color: COR.vermelhoEscuro, fontSize: '13px', fontWeight: 700 }}>
                            <Camera size={16} /> Ver fotos das evidências no relatório (PDF)
                        </a>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {checklist.naoAtende.map((it, i) => (
                            <div key={i} style={{ padding: '10px 0', borderTop: i ? `1px solid ${COR.bordaSuave}` : 'none' }}>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: COR.vermelho, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{it.area}</div>
                                <div style={{ fontSize: '14px', color: COR.texto, fontWeight: 600 }}>{it.pergunta}</div>
                                {it.observacao && <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>{it.observacao}</div>}
                            </div>
                        ))}
                    </div>
                </Secao>
            )}

            <Secao titulo="Histórico / evolução">
                {/* maiorMelhor: o checklist virou conformidade em 2026-09-04 — subir é bom,
                    igual a SA/TSV. Era `false` enquanto a métrica era risco. */}
                <HistoricoForm pontos={historico} maiorMelhor />
            </Secao>
        </div>
    );
}

// Painel de SA/TSV: última aplicação por SEÇÃO (pior no topo) + histórico.
function PainelSecoes({ dados, historico, titulo }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <Secao titulo={`Última aplicação — ${titulo}`}>
                {!dados ? (
                    <div style={{ color: '#999', fontSize: '14px' }}>Nenhuma aplicação registrada para a sua loja.</div>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                            {(() => { const cor = corPct(dados.total); return (
                                <span style={{ backgroundColor: cor.bg, color: cor.fg, fontWeight: 800, fontSize: '13px', borderRadius: '6px', padding: '3px 10px' }}>Total {fmt(dados.total)}%</span>
                            ); })()}
                            <span style={{ color: '#999', fontSize: '13px' }}>aplicada em {ddmm(dados.data)}</span>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#777', marginBottom: '10px' }}>Pontuação por seção (menor nota no topo)</div>
                        <SecaoBars secoes={dados.secoes} />
                    </>
                )}
            </Secao>
            <Secao titulo="Histórico / evolução">
                <HistoricoForm pontos={historico} maiorMelhor />
            </Secao>
        </div>
    );
}

// Barras por seção de SA/TSV (% de acerto; menor nota no topo, cor por faixa).
function SecaoBars({ secoes }) {
    if (!secoes || !secoes.length) return <span style={{ color: '#999' }}>—</span>;
    const ord = [...secoes].sort((a, b) => ((a.pct == null ? 101 : a.pct) - (b.pct == null ? 101 : b.pct)));
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {ord.map((s) => {
                const cor = s.pct == null ? CINZA : corPct(s.pct);
                const largura = s.pct == null ? 0 : Math.min(100, s.pct);
                return (
                    <div key={s.titulo} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ width: '210px', flexShrink: 0, fontSize: '13px', color: COR.texto, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={s.titulo}>{s.titulo}</span>
                        <div style={{ flex: 1, height: '10px', borderRadius: '5px', backgroundColor: '#f1f5f9', overflow: 'hidden', minWidth: '60px' }}>
                            <div style={{ width: `${largura}%`, height: '100%', backgroundColor: cor.fg }} />
                        </div>
                        <span style={{ width: '120px', flexShrink: 0, textAlign: 'right', fontSize: '12px', fontWeight: 700, color: cor.fg }}>{s.pct == null ? '—' : `${fmt(s.pct)}%`} <span style={{ color: '#aaa', fontWeight: 600 }}>({s.obtido}/{s.maximo})</span></span>
                    </div>
                );
            })}
        </div>
    );
}

function Secao({ titulo, children }) {
    return (
        <div style={{ backgroundColor: COR.branco, borderRadius: '10px', boxShadow: SOMBRA, padding: '18px' }}>
            <h2 style={{ margin: '0 0 14px', fontSize: '17px', fontWeight: 700, color: COR.texto }}>{titulo}</h2>
            {children}
        </div>
    );
}

// Barras de conformidade por área. A área PIOR fica no topo — o que a tela serve é
// "onde agir primeiro", e isso não mudou com a virada da métrica; o que mudou é que
// a pior passou a ser a de MENOR percentual. ⚠️ Área N/A (percentual null) vai para
// o FIM: ela não tem nota, e no topo pareceria a mais urgente.
function AreaBars({ areas }) {
    if (!areas || !areas.length) return <span style={{ color: '#999' }}>—</span>;
    const ordenadas = [...areas].sort((a, b) => (
        (a.percentualConformidade == null ? 101 : a.percentualConformidade) -
        (b.percentualConformidade == null ? 101 : b.percentualConformidade)
    ));
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {ordenadas.map((a) => {
                const cor = corClassificacao(a.classificacao);
                const na = a.percentualConformidade == null;
                const largura = na ? 0 : Math.min(100, a.percentualConformidade);
                return (
                    <div key={a.idArea} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ width: '210px', flexShrink: 0, fontSize: '13px', color: COR.texto, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.area}>{a.area}</span>
                        <div style={{ flex: 1, height: '10px', borderRadius: '5px', backgroundColor: '#f1f5f9', overflow: 'hidden', minWidth: '60px' }}>
                            <div style={{ width: `${largura}%`, height: '100%', backgroundColor: cor.fg }} />
                        </div>
                        <span style={{ width: '130px', flexShrink: 0, textAlign: 'right', fontSize: '12px', fontWeight: 700, color: cor.fg }}>{na ? 'N/A' : `${fmt(a.percentualConformidade)}% ${a.classificacao}`}</span>
                    </div>
                );
            })}
        </div>
    );
}

// Card de histórico com mini-gráfico (sparkline) da evolução da nota.
// Evolução das notas. Vai DENTRO de uma Secao (sem card próprio). Cada ponto do
// gráfico já traz valor + data; o destaque mostra a última nota + tendência.
function HistoricoForm({ titulo, pontos, maiorMelhor }) {
    const tem = pontos && pontos.length;
    const ultimo = tem ? pontos[pontos.length - 1].nota : null;
    const primeiro = tem ? pontos[0].nota : null;
    return (
        <div>
            {titulo && <div style={{ fontSize: '13px', fontWeight: 700, color: COR.texto, marginBottom: '8px' }}>{titulo}</div>}
            {!tem ? (
                <span style={{ color: '#999', fontSize: '13px' }}>Sem histórico.</span>
            ) : (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '22px', fontWeight: 800, color: COR.texto }}>{fmt(ultimo)}%</span>
                        {pontos.length > 1 && <Tendencia atual={ultimo} anterior={primeiro} maiorMelhor={maiorMelhor} />}
                        <span style={{ fontSize: '12px', color: '#999' }}>{pontos.length} aplicação(ões){pontos.length === 1 ? ` · ${ddmm(pontos[0].data)}` : ''}</span>
                    </div>
                    <Sparkline pontos={pontos} cor={maiorMelhor ? COR.verde : COR.vermelhoEscuro} />
                </>
            )}
        </div>
    );
}

// Mini-gráfico de linha (SVG) da evolução das notas, com o VALOR e a DATA
// rotulados em cada ponto. Escala uniforme (sem distorcer o texto).
function Sparkline({ pontos, cor }) {
    if (!pontos || pontos.length < 2) return null;   // 1 aplicação: o valor já aparece em destaque acima
    const vals = pontos.map((p) => p.nota);
    const min = Math.min(...vals); const max = Math.max(...vals); const range = max - min || 1;
    const W = 360; const H = 78; const padX = 24; const padTop = 18; const padBot = 16;
    const coords = pontos.map((p, i) => ({
        x: padX + (pontos.length === 1 ? (W - 2 * padX) / 2 : (i * (W - 2 * padX)) / (pontos.length - 1)),
        y: (H - padBot) - ((p.nota - min) / range) * (H - padTop - padBot),
        nota: p.nota, data: p.data,
    }));
    const linha = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
    return (
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', maxHeight: '96px' }}>
            <polyline points={linha} fill="none" stroke={cor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            {coords.map((c, i) => (
                <g key={i}>
                    <circle cx={c.x} cy={c.y} r="3" fill={cor} />
                    <text x={c.x} y={c.y - 7} textAnchor="middle" fontSize="11" fontWeight="700" fill={COR.texto}>{fmt(c.nota)}</text>
                    <text x={c.x} y={H - 3} textAnchor="middle" fontSize="9" fill="#aaa">{ddmm(c.data)}</text>
                </g>
            ))}
        </svg>
    );
}

// ---------------------------------------------------------------------
// Auxiliares compartilhados
// ---------------------------------------------------------------------
function Carregando() { return <div style={{ color: '#777' }}>Carregando…</div>; }
function CardResumo({ titulo, valor, rodape, alerta }) {
    return (
        <div style={{ backgroundColor: COR.branco, borderRadius: '10px', boxShadow: SOMBRA, padding: '16px 18px' }}>
            <div style={{ fontSize: '13px', color: '#777', fontWeight: 600 }}>{titulo}</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: alerta ? COR.vermelho : COR.texto, lineHeight: 1.2 }}>{valor}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>{rodape}</div>
        </div>
    );
}
function Tendencia({ atual, anterior, maiorMelhor }) {
    if (anterior == null || typeof atual !== 'number') return null;
    const delta = atual - anterior;
    if (delta === 0) return <Minus size={13} color={COR.cinza} />;
    const subiu = delta > 0;
    const bom = maiorMelhor ? subiu : !subiu;
    const Icone = subiu ? ArrowUp : ArrowDown;
    return <Icone size={13} color={bom ? COR.verde : COR.vermelho} />;
}
function CelulaPct({ c, vencidaDias }) {
    if (!c) return <span style={{ color: '#bbb' }}>—</span>;
    const cor = corPct(c.nota);
    const vencida = c.dias > vencidaDias;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ backgroundColor: cor.bg, color: cor.fg, fontWeight: 800, fontSize: '13px', borderRadius: '6px', padding: '2px 8px' }}>{fmt(c.nota)}%</span>
            <Tendencia atual={c.nota} anterior={c.anterior} maiorMelhor />
            <span style={{ fontSize: '12px', color: vencida ? COR.vermelho : '#999' }} title={vencida ? `sem aplicar há ${c.dias} dias` : ''}>{ddmm(c.data)}{vencida ? ' ⚠' : ''}</span>
        </div>
    );
}
function CelulaChecklist({ c, vencidaDias }) {
    if (!c) return <span style={{ color: '#bbb' }}>—</span>;
    const cor = corClassificacao(c.classificacao);
    const vencida = c.dias > vencidaDias;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ backgroundColor: cor.bg, color: cor.fg, fontWeight: 800, fontSize: '12px', borderRadius: '6px', padding: '2px 8px' }}>{c.classificacao || '—'} · {fmt(c.conformidade)}%</span>
            <Tendencia atual={c.conformidade} anterior={c.anteriorConformidade} maiorMelhor />
            <span style={{ fontSize: '12px', color: vencida ? COR.vermelho : '#999' }}>{ddmm(c.data)}{vencida ? ' ⚠' : ''}</span>
            {c.pdf && <a href={c.pdf} target="_blank" rel="noreferrer" title="Abrir relatório (PDF)" style={{ color: COR.vermelhoEscuro, display: 'inline-flex' }}><FileText size={15} /></a>}
        </div>
    );
}
function Ranking({ titulo, lojas, get, unidade, menorMelhor }) {
    const itens = lojas
        .map((l) => ({ loja: l.loja, valor: get(l) }))
        .filter((x) => typeof x.valor === 'number')
        .sort((a, b) => (menorMelhor ? a.valor - b.valor : b.valor - a.valor));
    // Posição com EMPATE, numeração DENSA (1, 2, 2, 3): empate divide a posição e
    // a próxima loja NÃO pula — só incrementa quando a nota muda.
    let posicao = 0; let valorAnterior = null;
    itens.forEach((x) => {
        const v = fmt(x.valor);
        if (valorAnterior === null || v !== valorAnterior) { posicao += 1; valorAnterior = v; }
        x.posicao = posicao;
    });
    return (
        <div style={{ backgroundColor: COR.branco, borderRadius: '10px', boxShadow: SOMBRA, padding: '14px 16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: COR.texto, marginBottom: '8px' }}>{titulo}</div>
            {itens.length === 0 ? (
                <div style={{ color: '#999', fontSize: '13px' }}>Sem dados.</div>
            ) : (
                itens.map((x, i) => (
                    <div key={x.loja} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderTop: i ? `1px solid ${COR.bordaSuave}` : 'none' }}>
                        <span style={{ width: '24px', color: '#999', fontWeight: 700, fontSize: '12px' }}>{x.posicao}º</span>
                        <span style={{ flex: 1, fontWeight: 600, color: COR.texto, fontSize: '13px' }}>{x.loja}</span>
                        <span style={{ fontWeight: 700, color: COR.texto, fontSize: '13px' }}>{fmt(x.valor)}{unidade}</span>
                    </div>
                ))
            )}
        </div>
    );
}
function Th({ children }) {
    return <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '12px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{children}</th>;
}
function Td({ children }) {
    return <td style={{ padding: '10px 16px' }}>{children}</td>;
}

export default IndicadoresFormularios;
